import { getLogger } from 'src/utils/datadog-logger';
import { abandonInFlightLoad, getThemeCacheKey, loadTheme } from './theme-cache';

const THEME_MODULE_PATH_PREFIX = 'themes/';
// Deliberate 3s bound trading pre-hydration wait against warm-SSR/cold-client mismatch rate.
export const PREWARM_TIMEOUT_MS = 3000;
export const PARTNER_THEME_SSR_FLAG = 'growth.ecomm.partner-theme-ssr.rollout';

const logger = getLogger('theme-prewarm');
const failedBootstrapLoads = new Set<string>();

export function hasBootstrapThemeFailed(scope: string, themeKey: string): boolean {
  return failedBootstrapLoads.has(getThemeCacheKey(scope, themeKey));
}

export function clearBootstrapThemeFailure(scope: string, themeKey: string): void {
  failedBootstrapLoads.delete(getThemeCacheKey(scope, themeKey));
}

export function clearBootstrapThemeFailures(): void {
  failedBootstrapLoads.clear();
}

export function getThemeKeyFromModulePath(modulePath: string | null | undefined): string | null {
  if (!modulePath?.startsWith(THEME_MODULE_PATH_PREFIX)) {
    return null;
  }
  const rawThemeKey = modulePath.slice(THEME_MODULE_PATH_PREFIX.length);
  if (!rawThemeKey || rawThemeKey !== rawThemeKey.trim() || rawThemeKey.includes('/')) {
    return null;
  }
  return rawThemeKey;
}

export type NextDataWithThemeBootstrap = {
  __NEXT_DATA__?: {
    props?: {
      pageProps?: {
        themeBootstrap?: {
          scope?: unknown;
          themeKey?: unknown;
        } | null;
      };
    };
  };
};

export type ThemeToBootstrap = { scope: string; themeKey: string };

function logBootstrapFailure(theme: ThemeToBootstrap, cacheKey: string, timedOut: boolean, timeoutMs: number): void {
  failedBootstrapLoads.add(cacheKey);
  logger.warn('Client theme bootstrap prewarm failed', {
    scope: theme.scope,
    themeKey: theme.themeKey,
    timedOut,
    timeoutMs,
  });
}

function isUsableKey(value: unknown): value is string {
  return typeof value === 'string' && value.length > 0 && value === value.trim();
}

export function extractNextDataThemeBootstrap(
  win: NextDataWithThemeBootstrap | undefined
): ThemeToBootstrap | undefined {
  const payload = win?.__NEXT_DATA__?.props?.pageProps?.themeBootstrap;
  if (payload && isUsableKey(payload.scope) && isUsableKey(payload.themeKey)) {
    return {
      scope: payload.scope,
      themeKey: payload.themeKey,
    };
  }
  return undefined;
}

export async function bootstrapClientTheme(
  theme: ThemeToBootstrap | undefined,
  timeoutMs: number = PREWARM_TIMEOUT_MS
): Promise<Record<string, unknown> | null> {
  if (!theme) {
    return null;
  }

  const cacheKey = getThemeCacheKey(theme.scope, theme.themeKey);
  let timeoutId: ReturnType<typeof setTimeout> | undefined;
  let timedOut = false;
  try {
    const loadPromise = loadTheme(theme.scope, theme.themeKey);
    const loaded = await Promise.race([
      loadPromise.catch(() => null),
      new Promise<null>((resolve) => {
        timeoutId = setTimeout(() => {
          timedOut = true;
          console.warn(`[theme-prewarm] Timed out after ${timeoutMs}ms prewarming ${theme.scope}/${theme.themeKey}`);
          abandonInFlightLoad(theme.scope, theme.themeKey, loadPromise);
          resolve(null);
        }, timeoutMs);
      }),
    ]);
    if (loaded) {
      failedBootstrapLoads.delete(cacheKey);
      return loaded;
    }
    logBootstrapFailure(theme, cacheKey, timedOut, timeoutMs);
    return null;
  } catch {
    logBootstrapFailure(theme, cacheKey, timedOut, timeoutMs);
    return null;
  } finally {
    if (timeoutId !== undefined) {
      clearTimeout(timeoutId);
    }
  }
}
